import { useMemo } from "react";
import {
  AudioReviewSegment,
  useAudioReview,
  useResolveAudioReview,
} from "@/hooks/useAudioReview";
import { AudioRegenChoices } from "@/components/AudioRegenChoices";

interface Props {
  slug: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Review queue for narration that's gone out of date after a wording edit.
 * Each item is resolved in place — regenerate now, on next play, or keep the
 * current audio — and drops off the list once a choice is made.
 */
export function NarrationReviewPanel({ slug, open, onClose }: Props) {
  const { data, isLoading } = useAudioReview(slug, open);
  const resolve = useResolveAudioReview(slug);

  // Group the flat queue by chapter, preserving the server's chapter→position order.
  const groups = useMemo(() => {
    const out: { title: string; items: AudioReviewSegment[] }[] = [];
    for (const s of data?.audioReviewSegments ?? []) {
      const title = s.chapterTitle ?? "—";
      const last = out[out.length - 1];
      if (last && last.title === title) last.items.push(s);
      else out.push({ title, items: [s] });
    }
    return out;
  }, [data]);

  if (!open) return null;
  const count = data?.audioReviewCount ?? 0;

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Narration to review ({count})</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            ✕
          </button>
        </div>
        <p className="mb-4 text-xs text-slate-500">
          These passages were edited after their narration was made. Choose what to do with each —
          “Keep current” marks it reviewed without re-recording.
        </p>

        {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
        {!isLoading && count === 0 && (
          <p className="text-sm text-slate-500">All narration is up to date. 🎧</p>
        )}

        {groups.map((g) => (
          <section key={g.title} className="mb-4">
            <h3 className="mb-2 text-sm font-medium text-slate-700">{g.title}</h3>
            <ul className="space-y-2">
              {g.items.map((s) => (
                <li key={s.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="mb-1 text-xs text-slate-400">{s.externalId}</div>
                  <p className="mb-2 line-clamp-3 font-serif text-sm text-slate-800">
                    {s.targetText}
                  </p>
                  <AudioRegenChoices
                    size="sm"
                    busy={resolve.isPending}
                    onChoose={(policy) => resolve.mutate({ segmentId: s.id, policy })}
                  />
                </li>
              ))}
            </ul>
          </section>
        ))}

        {resolve.isError && (
          <p className="mt-2 text-xs text-red-600">Couldn’t apply that — try again.</p>
        )}
      </div>
    </div>
  );
}
